import { useAccount, usePublicClient, useWriteContract } from "wagmi";
import { erc20Abi } from "viem";
import { npmAbi } from "../../lib/npm";
import { runTx } from "../TxStatus";

const friendlySim = (e) => {
  const m = e?.shortMessage || e?.message || "Simulation failed";
  if (/Price slippage check/i.test(m)) return "Simulation failed: price moved beyond your slippage limit. Refresh and retry.";
  if (/STF|transfer amount exceeds|insufficient/i.test(m)) return "Simulation failed: insufficient balance or allowance (or the token blocks/taxes transfers).";
  if (/Transaction too old/i.test(m)) return "Simulation failed: deadline passed.";
  if (/Not cleared/i.test(m)) return "Simulation failed: the NFT can only be burned after all liquidity is removed and everything owed is collected.";
  if (/Not approved/i.test(m)) return "Simulation failed: this wallet does not own (and is not approved for) this position NFT.";
  if (/fetch|network|timeout|HTTP request failed/i.test(m)) return "Robinhood Chain RPC request failed — check your connection and retry. Nothing was sent.";
  return `Simulation failed — nothing was sent. ${m}`;
};

// Simulates the NPM multicall from the user's wallet first, then asks the wallet to sign it.
export function useNpmTx() {
  const { address, chainId } = useAccount();
  const pc = usePublicClient({ chainId: 4663 });
  const { writeContractAsync } = useWriteContract();
  const npmExec = async ({ npm, calls, value, label, setTx }) => {
    if (chainId !== 4663) { setTx({ stage: "error", label, error: "Switch your wallet to Robinhood Chain (4663)." }); return null; }
    let request;
    try {
      ({ request } = await pc.simulateContract({ address: npm, abi: npmAbi, functionName: "multicall", args: [calls], value, account: address, chain: pc.chain }));
    } catch (e) { setTx({ stage: "error", label, error: friendlySim(e) }); return null; }
    return runTx({ pc, setTx, label, chainId: 4663, send: () => writeContractAsync({ ...request, chainId: 4663 }) });
  };
  const approve = async ({ token, spender, amount, setTx }) =>
    runTx({ pc, setTx, label: `Approve ${token.symbol}`, chainId: 4663, send: () => writeContractAsync({ chainId: 4663, address: token.address, abi: erc20Abi, functionName: "approve", args: [spender, amount] }) });
  const estimate = async ({ npm, calls, value }) => {
    try {
      const [gas, price] = await Promise.all([pc.estimateContractGas({ address: npm, abi: npmAbi, functionName: "multicall", args: [calls], value, account: address }), pc.getGasPrice()]);
      return { wei: gas * price, gas };
    } catch (e) { return { error: friendlySim(e) }; }
  };
  return { npmExec, approve, estimate, pc, address, chainId };
}
